import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sortBy } from 'lodash';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import Spinner from 'components/ui/Spinner';
import Table from 'components/v2/Table';
import AddButton from 'components/v2/buttons/AddButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import Checkbox from 'components/v2/forms/Checkbox';
import CargomaticSelect from 'components/v2/select';
import CargomaticTooltip from 'components/v2/Tooltip';
import { getReferenceNumberRequirements, upsertReferenceNumberRequirements, getReferenceNumbersMasterList, } from 'utils/UserServiceAPIUtils';
import { showToast } from 'stores/ToastStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';
const OPTIONS = [
    {
        value: 'BOOKING_TIME',
        localizationSlug: 'BOOKING_TIME',
    },
    {
        value: 'PRIOR_TO_PICKUP',
        localizationSlug: 'PRIOR_TO_PICKUP',
    },
    {
        value: 'PRIOR_TO_DELIVERY',
        localizationSlug: 'PRIOR_TO_DELIVERY',
    },
    {
        value: 'PRIOR_TO_INVOICING',
        localizationSlug: 'PRIOR_TO_INVOICING',
    },
];
const UPDATE_MODE = {
    ADD: 'add',
    EDIT: 'edit',
};
const TABLE_ACTIONS = {
    REMOVE: 'remove',
};
const MASTER_LIST_LIMIT = 100;
const NON_EDITABLE_MASTER_REFERENCES = ['master_bol', 'booking_number'];
export default function ReferenceNumbers({ id, className }) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [userReferenceNumbers, setUserReferenceNumbers] = useState([]);
    const [selectedReferenceNumbers, setSelectedReferenceNumbers] = useState([]);
    const [selectedRefNumber, setSelectedRefNumber] = useState();
    const [selectedRequiredAt, setSelectedRequiredAt] = useState();
    const [addRefNumber, setAddRefNumber] = useState();
    const [addRequiredAt, setAddRequiredAt] = useState();
    const [refresh, setRefresh] = useState({});
    const [tableAction, setTableAction] = useState('');
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [referenceNumberMasterList, setReferenceNumberMasterList] = useState([]);
    const toggleAllRef = useRef();
    const userCanEditSettings = SessionStore.hasFinanceAdminRole() ||
        SessionStore.hasShipperOnboardingRole();
    const user = SessionStore.getUser();
    async function fetchUserReferenceNumbers(id) {
        var _a, _b;
        try {
            const userResponse = await getReferenceNumberRequirements(id);
            if (!userResponse) {
                throw new Error('No response from API');
            }
            const referenceNumbers = sortBy((_b = (_a = userResponse === null || userResponse === void 0 ? void 0 : userResponse.data) === null || _a === void 0 ? void 0 : _a.getReferenceNumberRequirementsQuery) !== null && _b !== void 0 ? _b : [], (ref) => { var _a, _b; return (_b = (_a = ref === null || ref === void 0 ? void 0 : ref.reference_number) === null || _a === void 0 ? void 0 : _a.slug) === null || _b === void 0 ? void 0 : _b.toLowerCase(); });
            setUserReferenceNumbers(referenceNumbers === null || referenceNumbers === void 0 ? void 0 : referenceNumbers.map((refNumber) => {
                return Object.assign(Object.assign({}, refNumber), { id: refNumber.reference_number.slug, reference_number: refNumber.reference_number.slug });
            }));
        }
        catch (err) {
            showToast(t('ERROR_FETCHING_DATA'), null, 'error');
            console.error('Error during fetchUserReferenceNumbers: ', err.message);
        }
        finally {
            setIsLoading(false);
        }
    }
    async function fetchReferenceNumbersMasterList() {
        var _a, _b;
        try {
            setIsLoading(true);
            const response = await getReferenceNumbersMasterList(MASTER_LIST_LIMIT);
            const queryResult = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.getReferenceNumbersQuery;
            if (!queryResult) {
                throw new Error('No response from API');
            }
            const result = (_b = queryResult === null || queryResult === void 0 ? void 0 : queryResult.reference_numbers) !== null && _b !== void 0 ? _b : [];
            const slugs = result
                .filter((res) => !NON_EDITABLE_MASTER_REFERENCES.includes(res === null || res === void 0 ? void 0 : res.slug))
                .map((res) => res === null || res === void 0 ? void 0 : res.slug);
            const capitalizedSlugs = slugs === null || slugs === void 0 ? void 0 : slugs.map((slug) => {
                return {
                    name: slug,
                    localizationSlug: slug === null || slug === void 0 ? void 0 : slug.toUpperCase(),
                };
            });
            setReferenceNumberMasterList(capitalizedSlugs);
        }
        catch (err) {
            showToast(t('ERROR_FETCHING_DATA'), null, 'error');
            console.error('Error during fetchReferenceNumbersMasterList: ', err.message);
        }
        finally {
            setIsLoading(false);
        }
    }
    useEffect(() => {
        setIsLoading(true);
        fetchUserReferenceNumbers(id);
    }, [refresh]);
    useEffect(() => {
        fetchReferenceNumbersMasterList();
    }, []);
    function renderSpinner() {
        return React.createElement(Spinner, { showMessage: true, message: t('LOADING') });
    }
    function handleToggleAllCheck(checked) {
        var _a;
        if (checked) {
            const selected = userReferenceNumbers
                .filter((row) => !NON_EDITABLE_MASTER_REFERENCES.includes(row.id))
                .map((row) => row.id);
            setSelectedReferenceNumbers(selected);
        }
        else {
            setSelectedReferenceNumbers([]);
        }
        if ((_a = toggleAllRef === null || toggleAllRef === void 0 ? void 0 : toggleAllRef.current) === null || _a === void 0 ? void 0 : _a.indeterminate) {
            toggleAllRef.current.indeterminate = false;
        }
    }
    function isIndeterminate(selected) {
        const selectedCount = selected.length;
        return selectedCount > 0 && (userReferenceNumbers === null || userReferenceNumbers === void 0 ? void 0 : userReferenceNumbers.length) !== selectedCount;
    }
    function handleToggleCheck(id) {
        return function (checked) {
            var _a;
            let updatedSelections;
            if (checked) {
                updatedSelections = [...selectedReferenceNumbers, id];
                setSelectedReferenceNumbers(updatedSelections);
            }
            else {
                updatedSelections = selectedReferenceNumbers.filter((item) => item !== id);
                setSelectedReferenceNumbers(updatedSelections);
            }
            if ((_a = toggleAllRef === null || toggleAllRef === void 0 ? void 0 : toggleAllRef.current) === null || _a === void 0 ? void 0 : _a.indeterminate) {
                toggleAllRef.current.indeterminate = isIndeterminate(updatedSelections);
            }
        };
    }
    function handleAddReferenceNumber(value) {
        setAddRefNumber(value === null || value === void 0 ? void 0 : value.name);
    }
    function handleAddRequiredAt(value) {
        setAddRequiredAt(value === null || value === void 0 ? void 0 : value.value);
    }
    function handleSelectedRequiredAt(value) {
        setSelectedRequiredAt(value === null || value === void 0 ? void 0 : value.value);
    }
    function handleCancelEdit() {
        setIsEditing(false);
        setSelectedRefNumber();
        setSelectedRequiredAt();
    }
    function handleShowEditMode(row) {
        setIsEditing(true);
        setSelectedRefNumber(row.reference_number);
        setSelectedRequiredAt(row.required_at);
    }
    async function handleSave(modeOfUpdating) {
        var _a, _b, _c;
        try {
            const refNumberName = modeOfUpdating === UPDATE_MODE.ADD ? addRefNumber : selectedRefNumber;
            const requirement = {
                reference_number: modeOfUpdating === UPDATE_MODE.ADD ? addRefNumber : selectedRefNumber,
                required_at: modeOfUpdating === UPDATE_MODE.ADD
                    ? addRequiredAt
                    : selectedRequiredAt,
            };
            const filteredRequirements = (_a = userReferenceNumbers === null || userReferenceNumbers === void 0 ? void 0 : userReferenceNumbers.filter((userRefNum) => refNumberName !== userRefNum.id)) === null || _a === void 0 ? void 0 : _a.map((filteredRefNum) => {
                return {
                    reference_number: filteredRefNum.reference_number,
                    required_at: filteredRefNum.required_at,
                };
            });
            const response = await upsertReferenceNumberRequirements(id, [
                ...filteredRequirements,
                requirement,
            ], user._id);
            if (!response) {
                throw new Error('No response from API');
            }
            if ((_c = (_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.upsertReferenceNumberRequirementsMutation) === null || _c === void 0 ? void 0 : _c.success) {
                setRefresh(requirement);
            }
            showToast(t('EDIT_REFERENCE_NUMBER_SUCCESS'));
        }
        catch (err) {
            console.error('Error during upsertReferenceNumberRequirements: ', err.message);
            showToast(t('EDIT_REFERENCE_NUMBER_FAILURE'), null, 'error');
        }
    }
    function handleEditOnSave() {
        handleSave(UPDATE_MODE.EDIT);
        setIsEditing(false);
        setSelectedRefNumber();
        setSelectedRequiredAt();
    }
    function handleAddOnSave() {
        handleSave(UPDATE_MODE.ADD);
        setAddRefNumber();
        setAddRequiredAt();
    }
    function handleRemoveSingleReferenceNumber(row) {
        removeReferenceNumber([row.id]);
    }
    function handleRemoveBulkReferenceNumbers() {
        removeReferenceNumber(selectedReferenceNumbers);
        setShowConfirmDeleteModal(false);
    }
    function handleReferenceNumberTableSelect(e) {
        var _a;
        const action = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value;
        if (action === TABLE_ACTIONS.REMOVE) {
            setTableAction(action);
            setShowConfirmDeleteModal(true);
        }
    }
    function onHideConfirmDeleteModal() {
        setShowConfirmDeleteModal(false);
        handleToggleAllCheck(false);
    }
    async function removeReferenceNumber(referenceNumbersToRemove) {
        var _a, _b, _c;
        try {
            const filteredRequirements = (_a = userReferenceNumbers === null || userReferenceNumbers === void 0 ? void 0 : userReferenceNumbers.filter((userRefNum) => !referenceNumbersToRemove.includes(userRefNum.id))) === null || _a === void 0 ? void 0 : _a.map((filteredRefNum) => {
                return {
                    reference_number: filteredRefNum.reference_number,
                    required_at: filteredRefNum.required_at,
                };
            });
            const response = await upsertReferenceNumberRequirements(id, filteredRequirements, user._id);
            if (!response) {
                throw new Error('No response from API');
            }
            if ((_c = (_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.upsertReferenceNumberRequirementsMutation) === null || _c === void 0 ? void 0 : _c.success) {
                setSelectedRefNumber();
                setSelectedRequiredAt();
                setTableAction('');
                setRefresh({});
            }
            showToast(t('REMOVE_REFERENCE_NUMBERS_SUCCESS'));
        }
        catch (err) {
            console.error('Error during removeReferenceNumber: ', err.message);
            showToast(t('REMOVE_REFERENCE_NUMBERS_FAILURE'), null, 'error');
        }
    }
    function renderFooter() {
        return (React.createElement("tr", null,
            React.createElement("td", null, "\u00A0"),
            React.createElement("td", { colSpan: "1" },
                React.createElement(CargomaticSelect, { caption: t('CHOOSE_REFERENCE_NUMBER'), items: referenceNumberMasterList, showCaption: true, value: addRefNumber, valueKey: "name", textKey: "localizationSlug", onSelect: handleAddReferenceNumber })),
            React.createElement("td", { colSpan: "1" },
                React.createElement(CargomaticSelect, { items: OPTIONS, caption: t('CHOOSE_REQUIRED_AT'), showCaption: true, value: addRequiredAt, valueKey: "value", textKey: "localizationSlug", onSelect: handleAddRequiredAt })),
            React.createElement("td", null,
                React.createElement("div", { className: "flex justify-end" },
                    React.createElement(AddButton, { onClick: handleAddOnSave, disabled: !userCanEditSettings || !addRefNumber, color: "green" })))));
    }
    const columns = [
        {
            field: 'id',
            label: '',
            renderHeader: () => {
                var _a;
                return (React.createElement(Checkbox, { id: "id-renderHeader", checked: (selectedReferenceNumbers === null || selectedReferenceNumbers === void 0 ? void 0 : selectedReferenceNumbers.length) &&
                        (selectedReferenceNumbers === null || selectedReferenceNumbers === void 0 ? void 0 : selectedReferenceNumbers.length) ===
                            ((_a = userReferenceNumbers.filter((ref) => !NON_EDITABLE_MASTER_REFERENCES.includes(ref.id))) === null || _a === void 0 ? void 0 : _a.length), ref: toggleAllRef, onChange: handleToggleAllCheck }));
            },
            render: (value) => {
                return (React.createElement(Checkbox, { id: "id-render", disabled: NON_EDITABLE_MASTER_REFERENCES.includes(value), checked: selectedReferenceNumbers.includes(value), onChange: handleToggleCheck(value) }));
            },
        },
        {
            field: 'reference_number',
            label: t('REFERENCE_NUMBER'),
            render: (value) => React.createElement("div", { className: "w-32" }, t(value === null || value === void 0 ? void 0 : value.toUpperCase())),
        },
        {
            field: 'required_at',
            label: t('REQUIRED_AT'),
            render: (value, row, colIndex) => {
                if (isEditing && row.reference_number === selectedRefNumber) {
                    return (React.createElement(CargomaticSelect, { items: OPTIONS, value: selectedRequiredAt, valueKey: "value", textKey: "localizationSlug", onSelect: handleSelectedRequiredAt }));
                }
                return React.createElement("span", null, t(value));
            },
        },
        {
            field: 'action',
            label: '',
            render: (value, row, index) => {
                if (isEditing && row.reference_number === selectedRefNumber) {
                    return (React.createElement("div", { className: "flex items-center space-x-2 justify-end" },
                        React.createElement(ApproveButton, { onClick: handleEditOnSave }),
                        React.createElement(DeclineButton, { color: "gray", onClick: handleCancelEdit })));
                }
                if (NON_EDITABLE_MASTER_REFERENCES.includes(row.id)) {
                    return (React.createElement("div", { className: "flex justify-end" },
                        React.createElement(Dropdown, { useIcon: true },
                            React.createElement(Dropdown.Button, { disabled: true }, t('EDIT')),
                            React.createElement(Dropdown.Button, { disabled: true }, titleize(t('REMOVE'))))));
                }
                return (React.createElement("div", { className: "flex justify-end" },
                    React.createElement(Dropdown, { useIcon: true },
                        React.createElement(Dropdown.Button, { onClick: () => handleShowEditMode(row), disabled: !userCanEditSettings }, t('EDIT')),
                        React.createElement(Dropdown.Button, { onClick: () => handleRemoveSingleReferenceNumber(row), disabled: !userCanEditSettings }, titleize(t('REMOVE'))))));
            },
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "flex justify-between items-center mb-4" },
            React.createElement("header", { className: "flex items-center space-x-1" },
                React.createElement("h3", { className: "" }, t('REQUIRED_REFERENCE_NUMBERS')),
                React.createElement(CargomaticTooltip, { id: "required-reference-numbers" }, t('REQUIRED_REFERENCE_NUMBERS_TOOLTIP'))),
            React.createElement("select", { value: tableAction, onChange: handleReferenceNumberTableSelect, className: "form-select" },
                React.createElement("option", { value: "" }, t('SELECT_ACTION')),
                React.createElement("option", { value: "remove", disabled: !userCanEditSettings || !selectedReferenceNumbers.length }, titleize(t('REMOVE'))))),
        isLoading ? (renderSpinner()) : (React.createElement(Table, { columns: columns, footer: renderFooter, data: userReferenceNumbers, headers: columns, className: "overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg" })),
        showConfirmDeleteModal && (React.createElement(ConfirmModal, { show: showConfirmDeleteModal, title: t('REMOVE_REFERENCE_NUMBERS'), message: t('REMOVE_REFERENCE_NUMBERS_MESSAGE'), onHide: onHideConfirmDeleteModal, onConfirm: handleRemoveBulkReferenceNumbers }))));
}
